import { DragElementWrapper, DragSourceOptions } from 'react-dnd';

import * as React from 'react';
import { Button } from '@tock/shared/components/buttons/DeprecatedButton';
import { IconGrab, IconPin, IconTable } from '@tocktix/pantry';
import { Party } from 'types/service';
import { core } from '@tock/coretypes';
import { isEmpty } from 'es-toolkit/compat';
import { preventBubbleOutside } from '@tock/shared/utils/eventUtils';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import LinkButton from '@tock/shared/components/buttons/LinkButton';
import clsx from 'clsx';

interface Props {
  hideDrag?: boolean;
  isAssignable: boolean;
  isPartyStatusRefreshEnabled?: boolean;
  isReadOnly: boolean;
  party: Party;
  showClear?: boolean;
  visit?: core.Visit;
  connectDragSource: DragElementWrapper<DragSourceOptions>;
  onTogglePinned(): void;
  onToggleIsAssigningTables(): void;
  onClearTables?(party: Party): void;
}

const getRightButton = (
  hideDrag: boolean,
  isReadOnly: boolean,
  showClear: boolean,
  isAssignable: boolean,
  onClickClearTables: (event: React.SyntheticEvent) => void,
  connectDragSource: DragElementWrapper<DragSourceOptions>
) => {
  if (showClear) {
    return (
      <LinkButton variant="danger" onClick={onClickClearTables}>
        Clear
      </LinkButton>
    );
  }
  if (hideDrag || isReadOnly) {
    return (
      <div className="PartyPanel-assignTablesButton--grab" onClick={preventBubbleOutside}>
        <IconGrab />
      </div>
    );
  }

  return (
    <div
      className={clsx('PartyPanel-assignTablesButton--grab', {
        disabled: isReadOnly,
      })}
      onClick={preventBubbleOutside}
      ref={connectDragSource}
    >
      <IconGrab />
    </div>
  );
};

const PartyPanelSeatingBar = ({
  hideDrag,
  isAssignable,
  isPartyStatusRefreshEnabled = false,
  isReadOnly,
  showClear,
  visit,
  onClearTables,
  onTogglePinned,
  onToggleIsAssigningTables,
  party,
  connectDragSource,
}: Props) => {
  const onClickClearTables = (event: React.SyntheticEvent) => {
    preventBubbleOutside(event);
    if (onClearTables) {
      onClearTables(party);
    }
  };

  const isRemoveWalkinPinEnabled = useFeatureFlag('REMOVE_PIN_BUTTON_WALKIN');
  const isEmptyTables = isEmpty(party.tables);
  const isOrder =
    party?.purchase?.ticketType?.variety === 'ORDER_DELIVERY' ||
    party?.purchase?.ticketType?.variety === 'ORDER_PICKUP';
  if (isOrder) {
    return null;
  }
  const shouldShowPinButton = !!visit && (!isRemoveWalkinPinEnabled || !!visit.purchaseId);

  return (
    <div
      className={clsx('PartyPanel-assignTablesBar', {
        'PartyPanel-assignTablesBar--partyStatusRefresh': isPartyStatusRefreshEnabled,
      })}
    >
      {shouldShowPinButton && (
        <Button
          size="small"
          modifierClassName={clsx('PartyPanel-assignTables--pin', {
            pinned: visit.pinnedToTable,
            disabled: isEmptyTables,
          })}
          onClick={onTogglePinned}
        >
          <IconPin />
        </Button>
      )}
      <Button
        modifierClassName="PartyPanel-assignTablesButton-new"
        disabled={isReadOnly || !isAssignable}
        stretch
        variant="secondary"
        onClick={onToggleIsAssigningTables}
        data-testid="assign-table"
      >
        <div className="PartyPanel-assignTablesButton--content">
          <IconTable className="PartyPanel-assignTablesButton--tableIcon" />
          <div
            className={clsx('PartyPanel-assignTablesButton--tables', { disabled: isEmptyTables })}
            data-testid="guest-detail-preassigned-table-name"
          >
            {isEmptyTables ? 'Not assigned' : party.tables.map((table) => table.name).join(', ')}
          </div>
        </div>
      </Button>
      {getRightButton(
        !!hideDrag,
        isReadOnly,
        !!showClear,
        isAssignable,
        onClickClearTables,
        connectDragSource
      )}
    </div>
  );
};

export default PartyPanelSeatingBar;
